import React, { useState } from "react";
import {
    Dialog, DialogHeader, DialogBody, DialogFooter,
    Typography, IconButton
} from "@material-tailwind/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import axios from "axios";
import {isWorker} from "../../../utils/auth";

export default function ExportDialog({ isOpen, onClose, requestHeaders }) {

    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/arcado_admin";
    const [selectedPlatform, setSelectedPlatform] = useState('');
    const workerView = isWorker();

    const platforms = ['PS1', 'PS2', 'PS3', 'PS4', 'PS5'];

    const downloadFile = async (endpoint, filenameSuffix, extraParams = {}) => {
        try {
            const params = Object.fromEntries(
                Object.entries(extraParams).filter(([, v]) => v !== '' && v !== null && v !== undefined)
            );

            const response = await axios.get(`${API_URL}/purchased_items/${endpoint}`, {
                headers: requestHeaders,
                responseType: 'blob',
                params
            });
            const currentDate = new Date();
            const formattedDate = currentDate.toISOString().slice(0, 10).replace(/-/g, '_');
            const filename = `${formattedDate}_${filenameSuffix}.txt`;

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            setTimeout(() => {
                window.URL.revokeObjectURL(url);
                document.body.removeChild(link);
            }, 100);

            onClose();
        } catch (err) {
            console.error(`Error exporting ${filenameSuffix}:`, err);
        }
    };

    const exports = {
        manga: [
            { label: 'Unique', desc: 'Each title once', endpoint: 'export_available_manga_unique', suffix: 'available_manga_unique' },
            { label: 'Unique With Price', desc: 'With prices', endpoint: 'export_available_manga_unique_with_price', suffix: 'available_manga_unique_with_price' },
            { label: 'With Quantity', desc: 'With duplicate counts', endpoint: 'export_available_manga_with_quantity', suffix: 'available_manga_with_quantity' },
        ],
        games: [
            { label: 'Unique', desc: 'Each title once', endpoint: 'export_available_games_unique', suffix: 'available_games_unique' },
            { label: 'Unique With Price', desc: 'With prices', endpoint: 'export_available_games_unique_with_price', suffix: 'available_games_unique_with_price', hasPlatformFilter: true },
            { label: 'With Quantity', desc: 'With duplicate counts', endpoint: 'export_available_games_with_quantity', suffix: 'available_games_with_quantity' },
        ]
    };

    return (
        <Dialog size="xs" open={isOpen} handler={onClose} className="p-4 dark:bg-dark-800">
            <DialogHeader className="relative m-0 block pb-3">
                <Typography variant="h5" color="blue-gray" className="dark:text-gray-200">Export Data</Typography>
                <Typography className="mt-0.5 text-sm font-normal text-gray-500 dark:text-gray-400">Choose export format and type</Typography>
                <IconButton size="sm" variant="text" className="!absolute right-3.5 top-3.5" onClick={onClose}>
                    <XMarkIcon className="h-4 w-4 stroke-2" />
                </IconButton>
            </DialogHeader>
            <DialogBody className="p-0 px-4 pb-4">
                {/* Manga */}
                <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Manga</span>
                <div className="mt-1.5 mb-3 space-y-1">
                    {exports.manga.map(({ label, desc, endpoint, suffix }) => (
                        <div key={label} onClick={() => downloadFile(endpoint, suffix)}
                             className="flex items-center justify-between px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 cursor-pointer hover:bg-cyan-50 dark:hover:bg-cyan-900/20 transition-colors">
                            <div>
                                <span className="text-sm font-medium text-cyan-800 dark:text-cyan-300">{label}</span>
                                <span className="text-xs text-gray-400 dark:text-gray-500 ml-2">{desc}</span>
                            </div>
                            <span className="text-xs bg-gray-100 dark:bg-dark-700 text-gray-500 dark:text-gray-400 px-1.5 py-0.5 rounded">.txt</span>
                        </div>
                    ))}
                </div>

                {!workerView && (
                    <>
                        {/* Games */}
                        <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Games</span>
                        <div className="mt-1.5 space-y-1">
                            {exports.games.map(({ label, desc, endpoint, suffix, hasPlatformFilter }) => (
                                <div key={label}>
                                    <div
                                        onClick={() => downloadFile(
                                            endpoint,
                                            suffix,
                                            hasPlatformFilter ? { platform: selectedPlatform } : {}
                                        )}
                                        className="flex items-center justify-between px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 cursor-pointer hover:bg-cyan-50 dark:hover:bg-cyan-900/20 transition-colors"
                                    >
                                        <div>
                                            <span className="text-sm font-medium text-cyan-800 dark:text-cyan-300">{label}</span>
                                            <span className="text-xs text-gray-400 dark:text-gray-500 ml-2">{desc}</span>
                                        </div>
                                        <span className="text-xs bg-gray-100 dark:bg-dark-700 text-gray-500 dark:text-gray-400 px-1.5 py-0.5 rounded">.txt</span>
                                    </div>

                                    {hasPlatformFilter && (
                                        <div className="flex gap-1.5 flex-wrap px-1 pt-1.5 pb-0.5">
                                            <button
                                                onClick={() => setSelectedPlatform('')}
                                                className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${
                                                    selectedPlatform === ''
                                                        ? 'bg-cyan-800 text-white'
                                                        : 'bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-dark-600'
                                                }`}
                                            >
                                                All
                                            </button>
                                            {platforms.map(p => (
                                                <button
                                                    key={p}
                                                    onClick={() => setSelectedPlatform(p === selectedPlatform ? '' : p)}
                                                    className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${
                                                        selectedPlatform === p
                                                            ? 'bg-cyan-800 text-white'
                                                            : 'bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-dark-600'
                                                    }`}
                                                >
                                                    {p}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </DialogBody>
            <DialogFooter className="pt-3">
                <button onClick={onClose}
                        className="mv-primary-button">
                    <XMarkIcon className="h-4 w-4" />
                    Cancel
                </button>
            </DialogFooter>
        </Dialog>
    );
}
